(() => {
  const SWIPER_CSS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
  const SWIPER_JS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
  let swiperPromise;

  const asBool = (value) => value === true || value === 'true';
  const asNumber = (value, fallback) => {
    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
  };

  function ensureSwiper() {
    if (window.Swiper) return Promise.resolve(window.Swiper);
    if (swiperPromise) return swiperPromise;

    swiperPromise = new Promise((resolve, reject) => {
      if (!document.querySelector('link[data-glogo-swiper-css]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = SWIPER_CSS;
        link.dataset.glogoSwiperCss = '';
        document.head.appendChild(link);
      }

      const existing = document.querySelector('script[data-glogo-swiper-js]');
      if (existing) {
        if (window.Swiper) {
          resolve(window.Swiper);
          return;
        }
        existing.addEventListener('load', () => resolve(window.Swiper), { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = SWIPER_JS;
      script.async = true;
      script.dataset.glogoSwiperJs = '';
      script.addEventListener('load', () => resolve(window.Swiper), { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    return swiperPromise;
  }

  function destroySwiper(element) {
    if (element?.glogoSwiper && typeof element.glogoSwiper.destroy === 'function') {
      element.glogoSwiper.destroy(true, true);
    }
    if (element) element.glogoSwiper = null;
  }

  function mountHero(element, Swiper) {
    if (element.glogoSwiper) return;
    const root = element.closest('[data-glogo-section="hero"]');
    const slideCount = asNumber(element.dataset.slideCount, element.querySelectorAll('.swiper-slide').length);
    if (!slideCount) return;

    const canCycle = slideCount > 1;
    const navEnabled = asBool(element.dataset.showNavigation) && canCycle;
    const paginationEnabled = asBool(element.dataset.showPagination) && canCycle;
    const autoplayEnabled = asBool(element.dataset.autoplay) && canCycle;
    const wrap = element.closest('.gh-swiper-wrap');

    element.glogoSwiper = new Swiper(element, {
      initialSlide: 0,
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 0,
      speed: 550,
      loop: false,
      rewind: canCycle,
      watchOverflow: true,
      watchSlidesProgress: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      allowTouchMove: canCycle,
      navigation: navEnabled ? {
        prevEl: wrap?.querySelector(':scope > .gh-prev'),
        nextEl: wrap?.querySelector(':scope > .gh-next')
      } : false,
      pagination: paginationEnabled ? {
        el: element.querySelector('.gh-hero-pagination'),
        clickable: true
      } : false,
      autoplay: autoplayEnabled ? {
        delay: asNumber(element.dataset.autoplayDelay, 5000),
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      } : false,
      on: {
        init(swiper) {
          swiper.slideTo(0, 0, false);
          root?.setAttribute('data-swiper-ready', 'true');
        }
      }
    });
  }

  function mountProducts(element, Swiper) {
    if (element.glogoSwiper) return;
    const root = element.closest('[data-glogo-section="collection"]');
    const slideCount = asNumber(element.dataset.slideCount, element.querySelectorAll('.swiper-slide').length);
    if (!slideCount) return;

    const desktopCards = asNumber(element.dataset.desktopCards, 4);
    const tabletCards = asNumber(element.dataset.tabletCards, 3);
    const mobileCards = asNumber(element.dataset.mobileCards, 1.25);
    const navEnabled = asBool(element.dataset.showNavigation);
    const paginationEnabled = asBool(element.dataset.showPagination) && slideCount > 1;
    const wrap = element.closest('.gh-swiper-wrap');

    element.glogoSwiper = new Swiper(element, {
      slidesPerView: mobileCards,
      spaceBetween: 12,
      speed: 450,
      loop: false,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      grabCursor: slideCount > 1,
      navigation: navEnabled ? {
        prevEl: wrap?.querySelector(':scope > .gh-prev'),
        nextEl: wrap?.querySelector(':scope > .gh-next')
      } : false,
      pagination: paginationEnabled ? {
        el: element.querySelector('.gh-products-pagination'),
        clickable: true,
        dynamicBullets: slideCount > 8
      } : false,
      breakpoints: {
        520: { slidesPerView: tabletCards, spaceBetween: 14 },
        1120: { slidesPerView: desktopCards, spaceBetween: 14 }
      }
    });

    root?.setAttribute('data-swiper-ready', 'true');
  }

  async function mount(scope = document) {
    const elements = [...scope.querySelectorAll?.('[data-glogo-swiper]') || []];
    if (!elements.length) return;

    try {
      const Swiper = await ensureSwiper();
      if (!Swiper) return;
      elements.forEach((element) => {
        if (element.dataset.glogoSwiper === 'hero') mountHero(element, Swiper);
        if (element.dataset.glogoSwiper === 'products') mountProducts(element, Swiper);
      });
    } catch (error) {
      console.error('[Glogo] Swiper could not be loaded.', error);
    }
  }

  function mountSection(section) {
    if (!section) return;
    mount(section);
  }

  document.addEventListener('DOMContentLoaded', () => mount(document));

  document.addEventListener('shopify:section:load', (event) => {
    mountSection(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-glogo-swiper]').forEach(destroySwiper);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target.closest?.('[data-block-id]');
    if (!block) return;
    const hero = block.closest?.('[data-glogo-swiper="hero"]');
    const instance = hero?.glogoSwiper;
    if (!instance) return;
    const slides = [...hero.querySelectorAll('.swiper-slide[data-block-id]')];
    const index = slides.findIndex((slide) => slide.dataset.blockId === block.dataset.blockId);
    if (index < 0) return;
    instance.slideTo(index, 250);
  });

  window.GlogoHomepage = { mount };
})();
